import { ADMIN_RUNTIME_COMMANDS } from '../../app/runtime/adminRuntime';
import { prepareStorefrontLanguage } from '../../app/runtime/prepareStorefrontLanguage';

const BRIDGE_TIMEOUT_MS = 30000;
const LEASE_RENEW_MS = 30000;
const MAX_JOB_PAYLOAD_BYTES = 131072;
const MAX_JOB_PAYLOAD_BASE64 = 174764;

function requireMethod(value, method, label) {
  if (!value || typeof value[method] !== 'function') {
    throw new Error(`${label} is unavailable.`);
  }
  return value[method].bind(value);
}

async function bridge(bridgeClient, action, payload) {
  const send = requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge');
  const response = await send(action, payload, BRIDGE_TIMEOUT_MS);
  if (response?.result === 'error') {
    const error = new Error(response.error || `${action} failed.`);
    error.code = response.code || 'BRIDGE_FAILED';
    throw error;
  }
  return response;
}

function jobIdFrom(result) {
  return result?.jobId || result?.job_id || null;
}

function decodedClaimedJob(job) {
  if (!job?.payload_base64) return job;
  const encoded = String(job.payload_base64);
  if (encoded.length > MAX_JOB_PAYLOAD_BASE64 || !/^[A-Za-z0-9+/]+={0,2}$/.test(encoded)) {
    throw new Error('Storefront UI job payload encoding is invalid.');
  }
  let payload;
  try {
    const binary = window.atob(encoded);
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    if (bytes.byteLength > MAX_JOB_PAYLOAD_BYTES) {
      throw new Error('Storefront UI job payload exceeds its boundary.');
    }
    const json = typeof window.TextDecoder === 'function'
      ? new window.TextDecoder().decode(bytes)
      : decodeURIComponent(Array.from(bytes, byte => `%${byte.toString(16).padStart(2, '0')}`).join(''));
    payload = JSON.parse(json);
  } catch (_error) {
    throw new Error('Storefront UI job payload could not be decoded.');
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Storefront UI job payload is invalid.');
  }

  return { ...job, payload };
}

function safeFailure(error) {
  const rawCode = String(error?.code || 'CONTROL_PLANE_FAILED').toUpperCase();
  const code = /^[A-Z][A-Z0-9_]{2,63}$/.test(rawCode) ? rawCode : 'CONTROL_PLANE_FAILED';
  const rawMessage = String(error?.message || 'Storefront UI operation failed.')
    .replace(/\s+/g, ' ')
    .trim();
  return { code, message: rawMessage.slice(0, 300) || 'Storefront UI operation failed.' };
}

async function withLease({ bridgeClient, workerId, jobId }, operation) {
  let leaseLost = null;
  let renewing = false;
  const timer = setInterval(async () => {
    if (renewing || leaseLost) return;
    renewing = true;
    try {
      await bridge(bridgeClient, 'agent_ui.job.renew', {
        job_id: jobId,
        worker_id: workerId,
      });
    } catch (error) {
      leaseLost = error;
    } finally {
      renewing = false;
    }
  }, LEASE_RENEW_MS);
  try {
    const result = await operation(() => {
      if (leaseLost) throw leaseLost;
    });
    if (leaseLost) throw leaseLost;
    return result;
  } finally {
    clearInterval(timer);
  }
}

/** Process at most one product-owned storefront UI job. */
export async function processAgentUiJob({
  bridgeClient,
  gatewayClient,
  runtime,
  shopId,
  workerId,
}) {
  const claimed = await bridge(bridgeClient, 'agent_ui.job.claim', { worker_id: workerId });
  const job = decodedClaimedJob(claimed?.job);
  if (!job) return { status: 'idle' };

  try {
    const result = await withLease(
      { bridgeClient, workerId, jobId: job.id },
      async (assertLease) => {
        if (job.operation === 'apply_preview') {
          // Include the build language before Gateway signs the preview settings hash.
          await prepareStorefrontLanguage({ bridgeClient, gatewayClient, shopId });
          const apply = requireMethod(gatewayClient, 'applyAgentUiPreview', 'Gateway UI preview');
          const applied = await apply(shopId, job.payload?.candidate);
          assertLease();
          const build = await runtime.sendMessageWithResponse(
            ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW,
            { shopId },
          );
          assertLease();
          return {
            candidate_hash: applied.candidate_hash,
            preview_config_hash: applied.preview_config_hash,
            build_job_id: jobIdFrom(build),
            updated_product_grids: applied.updated_product_grids,
          };
        }
        if (job.operation === 'publish') {
          const verify = requireMethod(gatewayClient, 'verifyAgentUiPreview', 'Gateway UI verification');
          const expected = job.payload?.expected_preview_config_hash;
          const verification = await verify(shopId, expected);
          if (verification?.matches !== true) {
            const error = new Error('Preview settings changed after verification. Create a new UI preview.');
            error.code = 'PREVIEW_CHANGED';
            throw error;
          }
          assertLease();
          const publication = await runtime.sendMessageWithResponse(
            ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE,
            { shopId },
          );
          assertLease();
          return {
            candidate_hash: job.payload?.candidate?.candidate_hash,
            preview_config_hash: expected,
            publication_job_id: jobIdFrom(publication),
            published: true,
          };
        }
        const error = new Error('Unsupported storefront UI job operation.');
        error.code = 'UNSUPPORTED_OPERATION';
        throw error;
      },
    );
    await bridge(bridgeClient, 'agent_ui.job.complete', {
      job_id: job.id,
      worker_id: workerId,
      result,
    });
    return { status: 'completed', jobId: job.id, result };
  } catch (error) {
    const failure = safeFailure(error);
    try {
      await bridge(bridgeClient, 'agent_ui.job.fail', {
        job_id: job.id,
        worker_id: workerId,
        code: failure.code,
        message: failure.message,
      });
    } catch (_transitionError) {
      // A lost lease must not be overwritten by a stale browser worker.
    }
    return { status: 'failed', jobId: job.id, error: failure };
  }
}

export function createAgentUiWorkerId() {
  const bytes = new Uint8Array(12);
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return `admin-ui-${Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('')}`;
}
